import type { BuilderNode, BuilderState, BuilderStyle } from '../schemas/builder';

// Keep this pure contract identical to the widget copy; both run the same fixtures.
export type BuilderPresentationProperty = keyof BuilderStyle;
export type BuilderPresentationScalar = Exclude<BuilderStyle[keyof BuilderStyle], undefined>;
type StyleValues = Partial<Record<BuilderPresentationProperty, BuilderPresentationScalar>>;
export interface BuilderPresentationValue {
  value: BuilderPresentationScalar;
  source: string;
  inheritedFrom?: string;
  unresolved?: boolean;
}
export type BuilderPresentation = Partial<Record<BuilderPresentationProperty, BuilderPresentationValue>>;

const NORMAL: BuilderState = { active: false, disabled: false };
const inheritedProperties: readonly BuilderPresentationProperty[] = [
  'color', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing', 'textAlign', 'textTransform',
];

/** Initial declarations are hints, not inline resets of the browser's layout. */
const initialValues: StyleValues = {
  width: 'auto', height: 'auto', minWidth: 'auto', minHeight: 'auto', maxWidth: 'none', maxHeight: 'none',
  padding: '0px', paddingTop: '0px', paddingRight: '0px', paddingBottom: '0px', paddingLeft: '0px',
  margin: '0px', marginTop: '0px', marginRight: '0px', marginBottom: '0px', marginLeft: '0px',
  gap: 'normal', rowGap: 'normal', columnGap: 'normal', alignItems: 'normal', justifyContent: 'normal',
  flexDirection: 'row', flexWrap: 'nowrap', flexGrow: 0, flexShrink: 1, flexBasis: 'auto', alignSelf: 'auto',
  fontSize: '18px', fontWeight: 400, lineHeight: 1.4,
  letterSpacing: 'normal', textAlign: 'start', textTransform: 'none', textDecoration: 'none',
  color: 'var(--base-foreground-color)', backgroundColor: 'transparent', borderWidth: 'medium',
  borderStyle: 'none', borderColor: 'currentColor', borderRadius: '0px', opacity: 1, boxShadow: 'none',
  overflowX: 'visible', overflowY: 'visible', boxSizing: 'content-box',
  objectFit: 'fill', objectPosition: '50% 50%', backgroundImage: null, backgroundSize: 'auto',
  backgroundPosition: '0% 0%', backgroundRepeat: 'repeat',
};

export const BUILDER_PAGE_DEFAULTS: StyleValues = {
  backgroundColor: 'var(--base-background-color)', color: 'var(--base-foreground-color)',
  fontSize: '18px', lineHeight: 1.4,
};

const commonDefaults: StyleValues = {
  boxSizing: 'border-box', minWidth: '0px', backgroundSize: 'cover',
  backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
};
const containerDefaults: StyleValues = { gap: '16px', alignItems: 'stretch' };
export const BUILDER_IMAGE_DEFAULTS = { objectFit: 'contain', objectPosition: '50% 50%' } as const;
const blockDefaults: Record<BuilderNode['type'], StyleValues> = {
  row: { ...containerDefaults, flexDirection: 'row' },
  col: { ...containerDefaults, flexDirection: 'column' },
  text: {},
  data: {},
  image: { width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden', ...BUILDER_IMAGE_DEFAULTS },
  button: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '22px 16px', minHeight: '110px',
    fontSize: '28px', textAlign: 'inherit', letterSpacing: 'normal', textTransform: 'none',
    backgroundColor: 'var(--button-primary-normal-background-color)',
    color: 'var(--button-primary-normal-foreground-color)', borderWidth: '1px', borderStyle: 'solid',
    borderColor: 'var(--button-primary-normal-border-color)', borderRadius: 'var(--base-border-radius)',
  },
};

interface StyleLayer { style: StyleValues; source: string }

function styleLayers(node: BuilderNode, flags: BuilderState): StyleLayer[] {
  const label = node.type === 'row' || node.type === 'col' ? 'Container'
    : node.type[0].toUpperCase() + node.type.slice(1);
  const layers: StyleLayer[] = [
    { style: commonDefaults, source: 'Element default' },
    { style: blockDefaults[node.type], source: `${label} default` },
  ];
  if (node.type === 'button' && flags.active) layers.push({
    style: {
      backgroundColor: 'var(--button-primary-active-background-color)',
      color: 'var(--button-primary-active-foreground-color)', borderColor: 'var(--button-primary-active-border-color)',
    }, source: 'Button active default',
  });
  if (node.type === 'button' && flags.disabled) layers.push({ style: { opacity: 0.5 }, source: 'Button disabled default' });
  layers.push({ style: node.style ?? {}, source: 'Normal' });
  if (flags.active) layers.push({ style: node.states?.active?.style ?? {}, source: 'Active' });
  if (flags.disabled) layers.push({ style: node.states?.disabled?.style ?? {}, source: 'Disabled' });
  return layers;
}

const shorthands = ['padding', 'margin', 'gap'] as const;
const sides = ['Top', 'Right', 'Bottom', 'Left'] as const;
const properties = Object.keys(initialValues) as BuilderPresentationProperty[];

/** Never expand var() here: its substitution may contain a multi-value shorthand. */
function orderedProperties(style: StyleValues): BuilderPresentationProperty[] {
  return [...shorthands, ...properties.filter(property => !shorthands.some(short => short === property))]
    .filter(property => style[property] !== undefined);
}

function cssString(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\n\r\f]/g, character => `\\${character.charCodeAt(0).toString(16)} `)}"`;
}

function styleText(style: StyleValues, backgroundUrl?: string): string {
  return orderedProperties(style).map(property => {
    const value = style[property];
    const cssValue = property === 'backgroundImage'
      ? value && backgroundUrl ? `url(${cssString(backgroundUrl)})` : 'none'
      : String(value);
    return `${property.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}:${cssValue};`;
  }).join('');
}

export const BUILDER_PAGE_STYLE_TEXT = styleText(BUILDER_PAGE_DEFAULTS);

/** Replace the complete style attribute so removing a shorthand cannot leave stale longhands. */
export function builderNodeStyleText(node: BuilderNode, flags: BuilderState, backgroundUrl?: string): string {
  return styleLayers(node, flags).map(layer => styleText(layer.style, backgroundUrl)).join('');
}

function inheritedValue(value: BuilderPresentationValue, ancestor?: BuilderNode): BuilderPresentationValue {
  if (!ancestor || value.inheritedFrom || value.source === 'Page default') return { ...value };
  return { ...value, inheritedFrom: ancestor.id, source: `Inherited from ${ancestor.id} · ${value.source}` };
}

function knownSpacing(value: BuilderPresentationScalar): string[] | undefined {
  if (typeof value !== 'string') return undefined;
  const values = value.trim().split(/\s+/);
  return values.length <= 4 && values.every(part => /^(?:auto|0|-?(?:\d+(?:\.\d+)?|\.\d+)(?:px|rem|em|%))$/.test(part))
    ? values : undefined;
}

function applyLayer(result: BuilderPresentation, layer: StyleLayer, inherited: BuilderPresentation): void {
  for (const property of orderedProperties(layer.style)) {
    const value = layer.style[property]!;
    const entry: BuilderPresentationValue = { value, source: layer.source };
    result[property] = value === 'inherit' && inherited[property] ? { ...inherited[property] } : entry;
    if (property === 'padding' || property === 'margin') {
      const values = knownSpacing(value);
      const [top, right = top, bottom = top, left = right] = values ?? [];
      [top, right, bottom, left].forEach((side, index) => {
        result[`${property}${sides[index]}`] = side !== undefined
          ? { ...entry, value: side } : { ...entry, unresolved: true };
      });
    }
    if (property === 'gap') {
      // gap accepts a single length in builder.v1, but an opaque var may substitute two values.
      const unresolved = typeof value === 'string' && value.startsWith('var(');
      result.rowGap = { ...entry, ...(unresolved ? { unresolved: true } : {}) };
      result.columnGap = { ...entry, ...(unresolved ? { unresolved: true } : {}) };
    }
  }
}

function summarizeSpacing(result: BuilderPresentation, property: 'padding' | 'margin' | 'gap'): void {
  const entries = property === 'gap'
    ? [result.rowGap!, result.columnGap!] : sides.map(side => result[`${property}${side}`]!);
  const values = entries.map(entry => String(entry.value));
  const same = values.every(value => value === values[0]);
  const value = same ? values[0] : values.join(' ');
  const sameSource = entries.every(entry => entry.source === entries[0].source);
  result[property] = {
    value, source: sameSource ? entries[0].source : 'Mixed sources',
    ...(entries.some(entry => entry.unresolved) ? { unresolved: true } : {}),
  };
}

/** Resolve declarations and their sources, never pixel measurements or guessed CSS variables. */
export function resolveBuilderPresentation(
  node: BuilderNode, ancestors: readonly BuilderNode[] = [], flags: BuilderState = NORMAL,
): BuilderPresentation {
  let parent: BuilderPresentation = {};
  for (const property of inheritedProperties) {
    parent[property] = { value: BUILDER_PAGE_DEFAULTS[property] ?? initialValues[property]!, source: 'Page default' };
  }
  let parentNode: BuilderNode | undefined;
  let inButton = false;
  for (const current of [...ancestors, node]) {
    const result: BuilderPresentation = {};
    for (const property of properties) result[property] = { value: initialValues[property]!, source: 'CSS default' };
    const inherited: BuilderPresentation = {};
    for (const property of inheritedProperties) {
      if (parent[property]) inherited[property] = inheritedValue(parent[property], parentNode);
    }
    Object.assign(result, inherited);
    inButton ||= current.type === 'button';
    const currentFlags = current === node || inButton ? flags : NORMAL;
    for (const layer of styleLayers(current, currentFlags)) applyLayer(result, layer, inherited);
    for (const property of shorthands) summarizeSpacing(result, property);
    parent = result;
    parentNode = current;
  }
  return parent;
}
